import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Brand from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

const AUTH_IMG = "https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwY2xpbmljJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg2NjU2MTc2fDA&ixlib=rb-4.1.0&q=85";
const HOME = { admin: "/admin", therapist: "/painel", client: "/app" };

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await login(email, password);
      navigate(HOME[user.role] || "/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="w-full max-w-sm mx-auto">
          <Link to="/"><Brand className="mb-10" /></Link>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Bem-vindo(a) de volta</h1>
          <p className="text-muted-foreground mt-2">Acesse sua conta para continuar o acompanhamento.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email-input" className="mt-1.5" placeholder="voce@email.com" />
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required data-testid="login-password-input" className="mt-1.5" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="login-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            Ainda não tem conta?{" "}
            <Link to="/cadastro" className="text-primary font-medium hover:underline" data-testid="go-register-link">
              Cadastre-se
            </Link>
          </p>

          <div className="mt-8 p-4 rounded-xl bg-secondary/60 border border-border text-xs text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Bem-vindo(a) à Teraviva! Use uma conta de demonstração:</p>
            <p>Hipnoterapeuta: dra.ana@teraviva.com / terapeuta123</p>
            <p>Cliente: joao@teraviva.com / cliente123</p>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img src={AUTH_IMG} alt="Clínica minimalista" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-heading text-2xl font-medium leading-snug max-w-md">
            "Não deixe o acompanhamento terminar quando a sessão acaba."
          </p>
        </div>
      </div>
    </div>
  );
}
